import './index.scss'
import {useDispatch, useSelector} from 'react-redux'
import LoaderScreen from '../loader'
import {Link, NavLink, useLocation, useNavigate} from 'react-router-dom'
import {routes} from '../../../routes'
import Logo from '../../../assets/images/logo.svg'
import ClosedLogo from '../../../assets/images/logo-closed.svg'
import classNames from 'classnames'
import {Fragment, useEffect, useRef, useState} from 'react'
import {
    ArrowDownIcon,
    ArrowLeftIcon,
    ArrowUpIcon,
    BillingsIcon,
    HeartBeatsIcon,
    LogoutIcon,
    NotificationIcon,
    UptimeMonitoringIcon,
    ClosedIncidentManagementIcon,
    UserIcon,
    UsersIcon,
    StatuspageIcon,
    LogManagementIcon
} from '../../../icons'
import {Collapse} from 'react-collapse'
import CustomerHelper from '../../../utils/customer'
import Spinner from '../../core/spinner'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import {userLogOut} from '../../../stores/auth'
import {setSidebarIsActive, toggleSidebar} from '../../../stores/sidebar'
import Badge from '../../core/badge'
import {isNewFeatureEnabled} from '../../../utils/newFeature'
import TextHelper from '../../../utils/text'
import DarkModeSwitcher from '../../core/darkModeSwitcher'
import NavigationHelper from '../../../utils/navigation'
import localStorageKeys from '../../../utils/localStorageKeys'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'

const MIN_VALUE = 1
const INDEX_TWO = 2

const {
    locale,
    appEnv,
    isIntegrationEnabled,
    isPaymentEnabled,
    isSelfHosted
} = envConfig

const navElement = (icon, title, key, children, isNew = false, isBeta = false) => {
    return {icon, title, key, children, isNew, isBeta}
}

const subNavElement = (title, url, internalPagesUrls = [], isNew = false, isBeta = false, customActiveFunc = null) => {
    const lang = localStorage.getItem(localStorageKeys.LANGUAGE)
    const hasLanginUrl = lang && lang !== locale
    return {
        title,
        url: hasLanginUrl ? url[0] : url[1],
        internalPagesUrls: internalPagesUrls.map(pageUrl => Array.isArray(pageUrl) ?
            hasLanginUrl ? pageUrl[0] : pageUrl[1]
            : pageUrl),
        isNew,
        isBeta,
        customActiveFunc}
}

const Sidebar = ({trialMessage, infoMsgsHeight}) => {
    const text = useNamespaceTranslations('sidebar')
    const titles = useNamespaceTranslations('titles')
    const [accountMenuIsActive, setAccountMenuIsActive] = useState(false)
    const accountMenuRef = useRef(null)
    const sidebarRef = useRef(null)
    const [collapse, setCollapse] = useState({
        'monitoring': false,
        teams: false,
        notifications: false,
        heartbeats: false,
        'status-pages': false,
        'incident-management': false
    })
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const logoutIsProcessing = useSelector(state => state.auth.logoutIsProcessing)
    const customer = useSelector(state => state.sidebar.customer)
    const sidebarIsActive = useSelector(state => state.sidebar.sidebarIsActive)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const sidebarClassName = classNames({
        sidebar: true,
        'info-messages-in-view': infoMsgsHeight > 0,
        closed: !sidebarIsActive
    })

    const mobileWidth = 768
    const lang = localStorage.getItem(localStorageKeys.LANGUAGE)

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const stopPropagation = e => {
        e.stopPropagation()
    }

    const logOut = () => {
        dispatch(userLogOut())
    }

    const navArr = [
        navElement(<UptimeMonitoringIcon/>, titles.ping_home_console_monitoring, 'uptime-monitoring',
            [
                subNavElement(routes.resources.getTitle(), routes.resources.paths, [routes.monitoring.paths, routes.resource_integrations.paths]),
                subNavElement(routes.server_monitoring.getTitle(), routes.server_monitoring.paths, [routes.create_server_monitoring.paths, routes.update_server_monitoring.paths, routes.server_resource.paths]),
                subNavElement(routes.plans.getTitle(), ['/:lang/plans/#monitoring', '/plans/#monitoring'], ['#monitoring']),
                subNavElement(routes.usage.getTitle(), ['/:lang/usage/#monitoring', '/usage/#monitoring'], ['#monitoring'])
            ]),
        ((isSelfHosted && isNewFeatureEnabled()) || appEnv !== 'prod') &&  navElement(<LogManagementIcon customClassname='log-management-icon' />, titles.ping_home_console_log_management, 'log-management',
            [
                subNavElement(routes.log_groups_list.getTitle(), routes.log_groups_list.paths, [], false, false,
                    (location) =>  location.pathname === NavigationHelper.getLocalizedPath(routes.log_groups_list.paths)
                    || (location.pathname === NavigationHelper.getLocalizedPath(routes.logs_dashboard.paths) &&
      new URLSearchParams(location.search).has('log_group_id'))
                ),
                subNavElement(routes.logs_dashboard.getTitle(), routes.logs_dashboard.paths, [], false, false,
                    (location) => location.pathname === NavigationHelper.getLocalizedPath(routes.logs_dashboard.paths) &&
      !location.search)
            ], false, true),
        navElement(<StatuspageIcon/>, titles.ping_home_console_statuspages, 'status-pages',
            [
                subNavElement(routes.statuspages.getTitle(), routes.statuspages.paths, [routes.create_statuspage.paths, routes.update_statuspage.paths]),
                subNavElement(routes.statuspage_subscriptions.getTitle(), routes.statuspage_subscriptions.paths),
                subNavElement(routes.plans.getTitle(), ['/:lang/plans/#status-pages', '/plans/#status-pages'], ['#status-pages']),
                ...isNewFeatureEnabled() ? [subNavElement(routes.usage.getTitle(),
                    ['/:lang/usage/#status-pages', '/usage/#status-pages'], ['#status-pages'])] : []
            ],
            false, false),
        navElement(<ClosedIncidentManagementIcon/>, titles.ping_home_console_incident_management, 'incident-management',
            [
                subNavElement(routes.incidents.getTitle(), routes.incidents.paths),
                subNavElement(routes.rulesets.getTitle(), routes.rulesets.paths, [routes.create_ruleset.paths]),
                subNavElement(routes.on_call_scheduling.getTitle(), routes.on_call_scheduling.paths),
                subNavElement(routes.phone_calls.getTitle(), routes.phone_calls.paths, [routes.create_phone_call.paths], true),
                subNavElement(routes.plans.getTitle(), ['/:lang/plans/#incident-management', '/plans/#incident-management'], ['#incident-management']),
                subNavElement(routes.usage.getTitle(), ['/:lang/usage/#incident-management', '/usage/#incident-management'], ['#incident-management'])
            ],
            false, true),
        navElement(<HeartBeatsIcon/>, titles.ping_home_console_heartbeats, 'heartbeats',
            [
                subNavElement(routes.jobs.getTitle(), routes.jobs.paths, [routes.job.paths]),
                subNavElement(routes.job_events.getTitle(), routes.job_events.paths, [routes.job_events.paths])
            ]),
        navElement(<UsersIcon/>, isIntegrationEnabled ?
            `${titles.ping_home_console_teams} :intermediarySymbol ${titles.ping_home_console_integrations}` :
            titles.ping_home_console_teams, 'teams', [
            subNavElement(routes.teams.getTitle(), routes.teams.paths, [routes.members.paths]),
            subNavElement(routes.services.getTitle(), routes.services.paths),
            ...isIntegrationEnabled ? [subNavElement(routes.integrations.getTitle(), routes.integrations.paths)] : []

        ]),
        navElement(<NotificationIcon/>, titles.ping_home_console_notifications, 'notifications', [
            subNavElement(routes.notifications.getTitle(), routes.notifications.paths),
            subNavElement(routes.team_channels.getTitle(), routes.team_channels.paths),
            ...isNewFeatureEnabled() && !isSelfHosted ? [subNavElement(routes.automations.getTitle(), routes.automations.paths, [routes.automation_designer.paths], false, true)] : []
        ])
    ].filter(Boolean)

    const accountMenu = [
        navElement(<UserIcon/>, titles.ping_home_console_profile, 'profile', [
            isPaymentEnabled && subNavElement(routes.redeem_coupon.getTitle(), routes.redeem_coupon.paths),
            subNavElement(routes.apply_voucher.getTitle(), routes.apply_voucher.paths),
            subNavElement(routes.settings.getTitle(), routes.settings.paths),
            subNavElement(routes.security.getTitle(), routes.security.paths)
        ].filter(Boolean)),
        isPaymentEnabled && navElement(<BillingsIcon/>, titles.ping_home_console_billings, 'billings', [
            subNavElement(routes.subscriptions.getTitle(), routes.subscriptions.paths),
            subNavElement(routes.payment_methods.getTitle(), routes.payment_methods.paths)
        ])
    ].filter(Boolean)

    const getNavElementClassName = (item) => {
        const {key, children} = item
        const {pathname} = location
        const isActive = children.some(item => item.url === pathname) ||
            children.some(item => getPathNameMenu(item))

        return classNames({
            'nav-item': true,
            active: isActive && collapse?.[key]
        })
    }

    const getPathNameMenu = (item) => {
        if (typeof item?.customActiveFunc === 'function') {
            return item.customActiveFunc(location)
        }
        const index = item.url.includes(lang) ? INDEX_TWO : MIN_VALUE
        const pathnameSegment = location.pathname.split('/')?.[index]

        return (
            item.internalPagesUrls?.some(url => {
                const segment = url?.split('/')?.[index]
                return (pathnameSegment && pathnameSegment === segment) ||
                   (location.hash.includes(url) && location.pathname.includes(item.url))
            }) || item.url === location.pathname + location.hash
        )
    }

    const getSubItemClassName = (item) => {
        return classNames({
            active: getPathNameMenu(item)
        })
    }

    const generateSubMenu = navItem => {
        const {children} = navItem
        return <ul className="sub-menu">
            {children.map(item =>
                <li className="sub-item" key={item.title}>
                    <Link className={getSubItemClassName(item)}
                        to={item.url}>
                        {item.title}
                        {item.isNew && <Badge color="primary">New</Badge>}
                        {item.isBeta && <Badge color="secondary">beta</Badge>}
                    </Link>
                </li>
            )}
        </ul>
    }

    const handleCollapse = item => {
        if (sidebarIsActive) {
            setCollapse(prev => ({
                ...prev,
                [item.key]: !prev?.[item.key]
            }))
            return
        }
        navigate(item.children[0].url)
    }

    const getModifiedArr = arr => arr.map(navArrEl => ({...navArrEl,
        children: navArrEl.children.map(child => ({...child,
            url: child.url.replace(':lang', lang),
            internalPagesUrls: child.internalPagesUrls.map(internalUrl => internalUrl.replace(':lang', lang))
        }))}))

    const modifiedNavArr = getModifiedArr(navArr)
    const modifiedAccountMenu = getModifiedArr(accountMenu)

    useEffect(() => {
        const pathMenuItem = modifiedNavArr.find(navArrEl => navArrEl?.children?.some(item => getPathNameMenu(item)))

        setCollapse(prev => ({
            ...prev,
            [pathMenuItem?.key]: true
        }))
    }, [location?.pathname, location?.hash])

    const generateMenu = () => {
        return <nav className="navbar">
            <ul className="nav">
                {modifiedNavArr.map((item) => (
                    item && <Fragment key={item.key}>
                        <li onClick={() => handleCollapse(item)}
                            className={getNavElementClassName(item)}>
                            <div className="d-flex">
                                <span className="nav-icon">{item.icon}</span>
                                <span className="nav-title">
                                    <span>{TextHelper.createSectionTitle(item.title)}</span>
                                    {item.isNew && <Badge color="primary">New</Badge>}
                                    {item.isBeta && <Badge color="secondary">beta</Badge>}
                                </span>
                            </div>
                            <span className={classNames({
                                icon: true,
                                active: collapse?.[item.key]
                            })}>
                                <ArrowDownIcon/>
                            </span>
                        </li>
                        <Collapse isOpened={collapse?.[item.key]}>
                            {generateSubMenu(item)}
                        </Collapse>
                    </Fragment>
                ))}
            </ul>
        </nav>
    }

    const generateAccountMenu = () => {
        return (
            <div
                className={classNames({
                    'account-menu': true,
                    active: accountMenuIsActive
                })}>
                {modifiedAccountMenu.map((item, index) => (
                    item && <div key={index}>
                        <li className="account-menu-item">
                            <span className="d-flex">
                                {item.icon}
                            </span>
                            <span>{item.title}</span>
                        </li>
                        <ul className="account-menu-sub-items">
                            {item.children.map((menuItem, index) => (
                                menuItem && <li key={index} className="open-list-item">
                                    <NavLink className="open-sub-link" to={menuItem.url}>{menuItem.title}</NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                <li className="account-menu-single-item" onClick={stopPropagation}>
                    <div className="account-menu-link-item" onClick={logOut}>
                        <span className="d-flex logout">
                            <LogoutIcon/>
                        </span>
                        <span>{text.log_out}</span>
                        {logoutIsProcessing && <Spinner/>}
                    </div>
                </li>
            </div>
        )
    }

    useOnClickOutside(accountMenuRef, () => setAccountMenuIsActive(false))
    useOnClickOutside(sidebarRef, (e) => {
        const className = e.target.getAttribute('class')
        if (mobileWidth >= window.innerWidth && sidebarIsActive && className !== 'responsive-menu-icon') {
            dispatch(setSidebarIsActive(false))
        }
    })

    useEffect(() => {
        if (mobileWidth >= window.innerWidth) {
            dispatch(setSidebarIsActive(false))
        }
    }, [location])

    useEffect(() => {
        if (!isAuthenticated) return
        const height = infoMsgsHeight || 0
        document.documentElement.style.setProperty('--info-height', `${height}px`)
    }, [infoMsgsHeight, isAuthenticated])

    return isAuthenticated ? (
        <aside
            ref={sidebarRef}
            className={sidebarClassName}>
            <div className={`d-flex justify-content-between ${!sidebarIsActive ? 'flex-column' : ''}`}>
                <Link to={NavigationHelper.getLocalizedPath(routes.resources.paths)} className="logo">
                    <img width="116" height="31" src={sidebarIsActive ? Logo : ClosedLogo} alt="Pinghome"/>
                </Link>
                <DarkModeSwitcher/>
            </div>
            <div className={classNames('toggle-btn', {
                dark: isDarkMode
            })} onClick={() => dispatch(toggleSidebar())}>
                <span>
                    <ArrowLeftIcon/>
                </span>
            </div>
            {generateMenu()}
            <div className="account"
                ref={accountMenuRef}
                onClick={() => setAccountMenuIsActive(prev => !prev)}>
                <div className="responsive-account-menu">
                    <UserIcon/>
                    {generateAccountMenu()}
                </div>
                <div className="account-info">
                    <span
                        className="cover">{CustomerHelper.getUserFullNameFirstChars(customer)}</span>
                    <span className="customer-name">
                        <span className="customer-fullname">{CustomerHelper.getUserFullName(customer)}</span>
                        {trialMessage !== null && <span className="trial-info" dangerouslySetInnerHTML={{__html: trialMessage}}/>}
                    </span>
                    {generateAccountMenu()}
                </div>
                <div className="arrows">
                    <span>
                        <ArrowUpIcon/>
                    </span>
                    <span>
                        <ArrowDownIcon/>
                    </span>
                </div>
            </div>
        </aside>
    ) : <LoaderScreen/>
}

export default Sidebar
